import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { createBrowserRouter, RouterProvider, Outlet, useLocation, Navigate } from "react-router-dom";
import { initAnalytics, trackPageView } from "./utils/analytics";
import LandingPage from "./pages/LandingPage/LandingPage";
import ReferPage from "./pages/Refer/ReferPage";
import DashBoard from "./pages/DashBoard/Dashboard";
import ProfilePage from "./pages/Profile/ProfilePage";
import PrivacyPage from "./pages/Privacy/PrivacyPage";
import CreateVoucherPage from './pages/Vouchers/sections/CreateVoucher/CreateVoucherPage';
import VoucherPage from './pages/Vouchers/VoucherPage';
import PostTripConnectPage from './pages/PostTripConnect/PostTripConnectPage';
import TravellerFeedbackPage from './pages/TravellerFeedback/TravellerFeedbackPage';
import SupportPage from './pages/Support/SupportPage';
import TrackingPage from './pages/Tracking/TrackingPage';
import LiveTrackingMapPage from './pages/Tracking/sections/LiveTracking/sections/LiveTrackingMapPage';
import EditVoucherPage from './pages/Vouchers/sections/EditVoucher/EditVoucherPage';
import VoucherDashboardPage from './pages/Vouchers/sections/VoucherDashboard/VoucherDashboardPage';
import FullItineraryPage from './pages/Vouchers/FullItineraryPage';
import VoucherTemplatePage from './pages/Vouchers/sections/VoucherTemplate/VoucherTemplatePage';
import WaitingPage from './pages/Waiting/WaitingPage';
import AppBookingsPage from './pages/AppBookings/AppBookingsPage';
import WalletPage from './pages/Wallet/WalletPage';
import { AgentProvider } from "./context/agent/AgentContext";
import LiveTrackingState from "./context/tracking/LiveTrackingState";
import FlightTrackingState from "./context/tracking/FlightTrackingState";
import TrainTrackingState from "./context/tracking/TrainTrackingState";
import DashboardState from "./context/dashboard/DashboardState";
import { Toaster } from 'react-hot-toast';
import Page404 from './pages/404Page/Page404';
import { PartnerProvider } from './context/partnerManagement/PartnerContext';
import PartnerManagementPage from './pages/PartnerManagement/PartnerManagementPage';
import RoleManagementPage from './pages/RoleManagement/RoleManagementPage';
import AddUserPage from './pages/RoleManagement/sections/AddUser/AddUserPage';
import PredefinedItineraryPage from './pages/PredefinedItinerary/PredefinedItineraryPage';
import SubscriptionPage from "./pages/Profile/sections/Subscription/SubscriptionPage";
import CheckoutPage from "./pages/Profile/sections/Checkout/CheckoutPage";
import { SubscriptionProvider } from "./context/subscription/SubscriptionContext";
import RoleManagementState from "./context/roleManagement/RoleManagementState";
import PredefineItineraryState from "./context/predefineItinerary/PredefineItineraryState";
import AuthState from "./context/auth/AuthState";
import VoucherState from "./context/vouchers/VoucherState";
import AppBookingsState from "./context/appBookings/AppBookingsState";
import PostTripConnectState from "./context/postTripConnect/PostTripConnectState";
import ReferState from "./context/refer/ReferState";
import WaitingState from "./context/waiting/WaitingState";
import { PlacesProvider } from "./context/places/PlacesContext";

// Call initAnalytics immediately on entry
initAnalytics();

const RootLayout = () => {
  const location = useLocation();

  React.useEffect(() => {
    initAnalytics();
    trackPageView(location.pathname + location.search);
  }, [location]);

  return <Outlet />;
};

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: "/",
        element: <LandingPage />,
      },
      {
        path: "/login",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/signup",
        element: <Navigate to="/?tab=register" replace />,
      },
      {
        path: "/dashboard",
        element: <DashBoard />,
      },
      {
        path: "/tracking",
        element: <TrackingPage />,
      },
      {
        path: "/tracking/live-location",
        element: <TrackingPage />,
      },
      {
        path: "/tracking/map",
        element: <LiveTrackingMapPage />,
      },
      {
        path: "/tracking/flight-tracking",
        element: <TrackingPage />,
      },
      {
        path: "/tracking/train-tracking",
        element: <TrackingPage />,
      },
      {
        path: "/profile",
        element: <ProfilePage />,
      },
      {
        path: "/privacy",
        element: <PrivacyPage />,
      },
      {
        path: "/create-voucher",
        element: <CreateVoucherPage />,
      },
      {
        path: "/voucher",
        element: <VoucherPage />,
      },
      {
        path: "/feed-back",
        element: <TravellerFeedbackPage />,
      },
      {
        path: "/feedback",
        element: <TravellerFeedbackPage />,
      },
      {
        path: "/support",
        element: <SupportPage />,
      },
      {
        path: "/waiting",
        element: <WaitingPage />,
      },
      {
        path: "/bookings",
        element: <AppBookingsPage />,
      },
      {
        path: "/voucher-edit/:id",
        element: <EditVoucherPage />,
      },
      {
        path: "/voucher-dashboard/:id",
        element: <VoucherDashboardPage />,
      },
      {
        path: "/voucher-itinerary/:id",
        element: <FullItineraryPage />,
      },
      {
        path: "/voucher-template",
        element: <VoucherTemplatePage />,
      },
      {
        path: "/wallet",
        element: <WalletPage />,
      },
      {
        path: "/packages",
        element: <PostTripConnectPage />,
      },
      {
        path: "/notifications",
        element: <PostTripConnectPage />,
      },
      {
        path: "/enquiries",
        element: <PostTripConnectPage />,
      },
      {
        path: "/post-connect",
        element: <PostTripConnectPage />,
      },
      {
        path: "/partners",
        element: <PartnerManagementPage />,
      },
      {
        path: "/subscription",
        element: <SubscriptionPage />,
      },
      {
        path: "/checkout",
        element: <CheckoutPage />,
      },
      /* Itinerary locked for all plans - yields 404 until enabled
      {
        path: "/your-itinerary",
        element: <PredefinedItineraryPage />,
      },
      {
        path: "/predefined-itinerary",
        element: <PredefinedItineraryPage />,
      },
      */
      {
        path: "/role-management",
        element: <RoleManagementPage />,
      },
      {
        path: "/role-management/add",
        element: <AddUserPage />,
      },
      {
        path: "*",
        element: <Page404 />,
      }
    ]
  },
  {
    path: "/refer",
    element: <ReferPage />,
  },
  {
    path: "/profile",
    element: <ProfilePage />,
  },
  {
    path: "/privacy",
    element: <PrivacyPage />,
  },
  {
    path: "/create-voucher",
    element: <CreateVoucherPage />,
  },
  {
    path: "/voucher",
    element: <VoucherPage />,
  },
  {
    path: "/feed-back",
    element: <TravellerFeedbackPage />,
  },
  {
    path: "/feedback",
    element: <TravellerFeedbackPage />,
  },
  {
    path: "/support",
    element: <SupportPage />,
  },
  {
    path: "/waiting",
    element: <WaitingPage />,
  },
  {
    path: "/bookings",
    element: <AppBookingsPage />,
  },
  {
    path: "/voucher-edit/:id",
    element: <EditVoucherPage />,
  },
  {
    path: "/voucher-dashboard/:id",
    element: <VoucherDashboardPage />,
  },
  {
    path: "/voucher-itinerary/:id",
    element: <FullItineraryPage />,
  },
  {
    path: "/wallet",
    element: <WalletPage />,
  },
  {
    path: "/packages",
    element: <PostTripConnectPage />,
  },
  {
    path: "/notifications",
    element: <PostTripConnectPage />,
  },
  {
    path: "/enquiries",
    element: <PostTripConnectPage />,
  },
  {
    path: "/post-connect",
    element: <PostTripConnectPage />,
  },
  {
    path: "/partners",
    element: <PartnerManagementPage />,
  },
  {
    path: "/subscription",
    element: <SubscriptionPage />,
  },
  {
    path: "/checkout",
    element: <CheckoutPage />,
  },
  /* Itinerary locked for all plans - yields 404 until enabled
  {
    path: "/your-itinerary",
    element: <PredefinedItineraryPage />,
  },
  {
    path: "/predefined-itinerary",
    element: <PredefinedItineraryPage />,
  },
  */
  {
    path: "/role-management",
    element: <RoleManagementPage />,
  },
  {
    path: "/role-management/add",
    element: <AddUserPage />,
  },
  {
    path: "*",
    element: <Page404 />,
  }
]);

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <AgentProvider>
      <LiveTrackingState>
        <FlightTrackingState>
          <TrainTrackingState>
            <DashboardState>
            <Toaster
              position="top-center"
              reverseOrder={false}
              containerStyle={{ zIndex: 99999 }}
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#fff',
                  color: '#363636',
                  fontWeight: '500',
                  fontSize: '14px',
                  borderRadius: '12px',
                  padding: '12px 20px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
                success: {
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />

            <AuthState>
              <VoucherState>
                <AppBookingsState>
                  <PostTripConnectState>
                    <ReferState>
                      <WaitingState>
                        <RoleManagementState>
                          <PredefineItineraryState>
                            <PartnerProvider>
                              <SubscriptionProvider>
                                <PlacesProvider>
                                  <RouterProvider router={router} />
                                </PlacesProvider>
                              </SubscriptionProvider>
                            </PartnerProvider>
                          </PredefineItineraryState>
                        </RoleManagementState>
                      </WaitingState>
                    </ReferState>
                  </PostTripConnectState>
                </AppBookingsState>
              </VoucherState>
            </AuthState>
          </DashboardState>
        </TrainTrackingState>
      </FlightTrackingState>
    </LiveTrackingState>
  </AgentProvider>
  </React.StrictMode>
);